import { Mic, RotateCcw, Send, Square, Type, Video, X } from 'lucide-react';
import { useRef, useState } from 'react';

// ── Utilitaire : Blob → base64 (pour transmission réseau) ─────────────────
const blobVersBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result); // "data:audio/webm;base64,..."
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export default function VocalOrTextInput({
  stepId,
  duration = 120,
  onSubmit,
  label = "Consigner ma réflexion",
}) {
  const [isOpen, setIsOpen]         = useState(false);
  const [texte, setTexte]           = useState('');
  const [mode, setMode]             = useState(null);        
  const [enregistrement, setEnregistrement] = useState(false);
  const [mediaUrl, setMediaUrl]     = useState(null);        
  const [mediaBlob, setMediaBlob]   = useState(null);        
  const [dureeEnreg, setDureeEnreg] = useState(0);           
  const [soumis, setSoumis]         = useState(false);
  const [transmission, setTransmission] = useState(false);  

  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const videoRef         = useRef(null);
  const streamRef        = useRef(null);
  const timerRef         = useRef(null);

  const demarrerEnregistrement = async (type) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: type === 'video',
      });
      streamRef.current = stream;

      if (type === 'video' && videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const mimeType = type === 'video' ? 'video/webm' : 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setMediaBlob(blob);
        setMediaUrl(URL.createObjectURL(blob));
        setEnregistrement(false);
        clearInterval(timerRef.current);
        stream.getTracks().forEach(t => t.stop());
        if (videoRef.current) videoRef.current.srcObject = null;
      };

      recorder.start();
      setEnregistrement(true);
      setMode(type);
      setDureeEnreg(0);
      setMediaUrl(null);
      setMediaBlob(null);

      // Timer affiché (pas d'arrêt auto — l'utilisateur choisit)
      timerRef.current = setInterval(() => {
        setDureeEnreg(d => d + 1);
      }, 1000);

    } catch {
      alert("Accès au micro/caméra refusé. Basculement en mode texte.");
      setMode('text');
    }
  };

  const arreterEnregistrement = () => {
    if (mediaRecorderRef.current && enregistrement) {
      mediaRecorderRef.current.stop();
    }
    clearInterval(timerRef.current);
  };

  const recommencer = () => {
    if (mediaUrl) URL.revokeObjectURL(mediaUrl);
    setMediaUrl(null);
    setMediaBlob(null);
    setDureeEnreg(0);
  };

  const soumettre = async () => {
    if (!texte.trim() && !mediaBlob) return;
    setTransmission(true);

    try {
      let mediaBase64 = null;
      if (mediaBlob) {
        mediaBase64 = await blobVersBase64(mediaBlob);
        console.log("[VOCAL_INPUT] base64 généré – longueur:", mediaBase64.length);
        console.log("[VOCAL_INPUT] mime type:", mediaBlob.type);
      }
      
      const payload = {
        stepId,
        text: texte.trim() || null,
        mediaBase64,
        mediaUrl,
        mediaType: mode,
        dureeEnreg,
      };

      console.log("[VOCAL_INPUT → onSubmit] payload complet envoyé :", payload);

      await onSubmit?.(payload);

      setTexte('');
      setMediaUrl(null);
      setMediaBlob(null);
      setMode(null);
      setIsOpen(false);
      setSoumis(true);
    } catch (err) {
      console.error('❌ Erreur transmission harvest:', err);
    } finally {
      setTransmission(false);
    }
  };

  const formaterDuree = (sec) => {
    const m = Math.floor(sec / 60).toString().padStart(2, '0');
    const s = (sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  if (soumis) {
    return (
      <div className="mt-4 flex items-center gap-3 px-5 py-3 rounded-xl"
        style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)' }}
      >
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        <span className="text-[9px] font-black uppercase tracking-[0.25em] font-mono text-emerald-400/80">
          Données_Transmises
        </span>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="mt-4 w-full flex items-center justify-between px-5 py-3.5 rounded-xl transition-all group active:scale-[0.99]"
        style={{ border: '1px dashed rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.02)' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)'; e.currentTarget.style.background = 'rgba(99,102,241,0.04)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
      >
        <div className="flex items-center gap-3">
          <div className="w-1 h-4 rounded-full bg-indigo-500/40" />
          <span className="text-[9px] font-black uppercase tracking-[0.25em] font-mono text-white/30 group-hover:text-white/60 transition-colors">
            {label}
          </span>
        </div>
        <Send size={11} className="text-white/10 group-hover:text-indigo-400 transition-colors" />
      </button>
    );
  }

  return (
    <div className="mt-4 rounded-2xl overflow-hidden"
      style={{ border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.35)' }}
    >
      {/* ── En-tête ── */}
      <div className="flex items-center justify-between px-4 sm:px-5 py-3"
        style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
      >
        <div className="flex items-center gap-2">
          <div className="w-1 h-3 rounded-full" style={{
            background: mode === 'audio' ? '#10b981'
              : mode === 'video' ? '#f43f5e'
              : mode === 'text' ? '#6366f1'
              : 'rgba(255,255,255,0.2)'
          }} />
          <span className="text-[9px] font-black uppercase tracking-[0.25em] font-mono text-white/30">
            {mode === 'video' && enregistrement ? 'Vidéo_En_Cours'
              : mode === 'audio' && enregistrement ? 'Audio_En_Cours'
              : mode === 'text' ? 'Saisie_Texte'
              : mode && !enregistrement && mediaUrl ? 'Aperçu_Enregistrement'
              : 'Choisir_Format'}
          </span>
        </div>
        <button
          onClick={() => {
            if (enregistrement) arreterEnregistrement();
            if (mediaUrl) URL.revokeObjectURL(mediaUrl);
            setIsOpen(false);
            setMode(null);
            setMediaUrl(null);
            setMediaBlob(null);
            setTexte('');
            clearInterval(timerRef.current);
          }}
          className="p-1.5 rounded-lg text-white/20 hover:text-white/60 transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      <div className="p-4 sm:p-5 space-y-4">

        {/* ── SÉLECTION DU MODE ── */}
        {!mode && (
          <div className="grid grid-cols-3 gap-2">
            {[
              { id: 'video', Icon: Video,  label: 'Vidéo',  couleur: '#f43f5e', bg: 'rgba(244,63,94,0.08)',  border: 'rgba(244,63,94,0.25)' },
              { id: 'audio', Icon: Mic,    label: 'Audio',  couleur: '#10b981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.25)' },
              { id: 'text',  Icon: Type,   label: 'Texte',  couleur: '#6366f1', bg: 'rgba(99,102,241,0.08)', border: 'rgba(99,102,241,0.25)' },
            ].map(({ id, Icon, label: lbl, couleur, bg, border }) => (
              <button
                key={id}
                onClick={() => id === 'text' ? setMode('text') : setMode(id)}
                // py-5 → py-4 sur mobile
                className="flex flex-col items-center gap-2 sm:gap-3 py-4 sm:py-5 rounded-xl transition-all active:scale-95"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                onMouseEnter={e => { e.currentTarget.style.background = bg; e.currentTarget.style.border = `1px solid ${border}`; e.currentTarget.style.color = couleur; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.border = '1px solid rgba(255,255,255,0.06)'; e.currentTarget.style.color = ''; }}
              >
                <Icon size={16} className="text-white/30" />
                <span className="text-[9px] font-black uppercase tracking-widest font-mono text-white/30">
                  {lbl}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* ── MODE AUDIO — prêt à démarrer ── */}
        {mode === 'audio' && !enregistrement && !mediaUrl && (
          <div className="text-center space-y-4">
            <div className="py-5 sm:py-6 rounded-xl" style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)' }}>
              <Mic size={28} className="text-emerald-500/50 mx-auto mb-3" />
              <p className="text-[9px] font-mono uppercase tracking-widest text-white/30">
                Cliquez pour démarrer l'enregistrement audio
              </p>
            </div>
            <button
              onClick={() => demarrerEnregistrement('audio')}
              className="w-full py-3 sm:py-3.5 rounded-xl font-black text-[10px] uppercase tracking-widest text-white transition-all active:scale-[0.98]"
              style={{ background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.4)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(16,185,129,0.2)'; }}
            >
              <span className="flex items-center justify-center gap-2">
                <Mic size={14} /> Démarrer_Enregistrement
              </span>
            </button>
          </div>
        )}

        {/* ── MODE VIDEO — prêt à démarrer ── */}
        {mode === 'video' && !enregistrement && !mediaUrl && (
          <div className="text-center space-y-4">
            <div className="py-5 sm:py-6 rounded-xl" style={{ background: 'rgba(244,63,94,0.05)', border: '1px solid rgba(244,63,94,0.15)' }}>
              <Video size={28} className="text-rose-500/50 mx-auto mb-3" />
              <p className="text-[9px] font-mono uppercase tracking-widest text-white/30">
                Cliquez pour démarrer l'enregistrement vidéo
              </p>
            </div>
            <button
              onClick={() => demarrerEnregistrement('video')}
              className="w-full py-3 sm:py-3.5 rounded-xl font-black text-[10px] uppercase tracking-widest text-white transition-all active:scale-[0.98]"
              style={{ background: 'rgba(244,63,94,0.2)', border: '1px solid rgba(244,63,94,0.4)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244,63,94,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(244,63,94,0.2)'; }}
            >
              <span className="flex items-center justify-center gap-2">
                <Video size={14} /> Démarrer_Enregistrement
              </span>
            </button>
          </div>
        )}

        {/* ── ENREGISTREMENT EN COURS ── */}
        {enregistrement && (
          <div className="space-y-4">
            {/* Preview vidéo live */}
            {mode === 'video' && (
              <div className="relative rounded-xl overflow-hidden" style={{ border: '1px solid rgba(244,63,94,0.3)' }}>
                <video ref={videoRef} className="w-full aspect-video object-cover bg-black" muted />
                <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-full" style={{ background: 'rgba(0,0,0,0.7)' }}>
                  <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[8px] font-mono font-bold text-white uppercase tracking-widest">
                    REC {formaterDuree(dureeEnreg)}
                  </span>
                </div>
              </div>
            )}

            {/* Visualiseur audio */}
            {mode === 'audio' && (
              <div className="flex items-center gap-3 px-4 sm:px-5 py-4 rounded-xl" style={{ background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)' }}>
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse shrink-0" />
                <div className="flex items-end gap-[3px] h-8 flex-1 overflow-hidden">
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1 rounded-full bg-emerald-500 animate-bounce"
                      style={{
                        animationDelay: `${i * 0.05}s`,
                        height: `${12 + Math.sin(i * 0.8) * 10}px`,
                        opacity: 0.6 + Math.cos(i) * 0.4
                      }}
                    />
                  ))}
                </div>
                <span className="text-[10px] font-mono font-black text-emerald-400 tabular-nums shrink-0">
                  {formaterDuree(dureeEnreg)}
                </span>
              </div>
            )}

            {/* Bouton STOP */}
            <button
              onClick={arreterEnregistrement}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-black text-[11px] uppercase tracking-widest text-white transition-all active:scale-[0.98]"
              style={{ background: 'rgba(239,68,68,0.2)', border: '2px solid rgba(239,68,68,0.5)' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.35)'; e.currentTarget.style.boxShadow = '0 0 20px rgba(239,68,68,0.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <Square size={14} fill="currentColor" /> Arrêter_Enregistrement
            </button>
          </div>
        )}

        {/* ── APERÇU APRÈS ENREGISTREMENT ── */}
        {mediaUrl && !enregistrement && (
          <div className="space-y-3">
            <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
              {mode === 'video'
                ? <video controls src={mediaUrl} className="w-full aspect-video object-cover bg-black" />
                : (
                  <div className="p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <audio controls src={mediaUrl} className="w-full h-8" style={{ filter: 'invert(1) hue-rotate(180deg)' }} />
                  </div>
                )
              }
            </div>

            <div className="flex items-center justify-between px-1">
              <span className="text-[8px] font-mono text-white/30 uppercase">
                Durée : {formaterDuree(dureeEnreg)}
              </span>
              <button
                onClick={recommencer}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[8px] font-black uppercase tracking-wider transition-all active:scale-95"
                style={{ border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
              >
                <RotateCcw size={10} /> Recommencer
              </button>
            </div>
          </div>
        )}

        {/* ── SAISIE TEXTE ── */}
        {mode === 'text' && (
          <textarea
            rows={4}
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder="Entrer la réflexion ici..."
            className="w-full p-4 rounded-xl text-sm font-mono text-white outline-none resize-none"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'white',
              caretColor: '#6366f1',
            }}
            onFocus={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.5)'; }}
            onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
          />
        )}

        {/* ── BOUTON TRANSMETTRE ── */}
        {(texte.trim() || mediaUrl) && !enregistrement && (
          <button
            onClick={soumettre}
            disabled={transmission}
            className="w-full flex items-center justify-center gap-2 py-3 sm:py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest text-white transition-all disabled:opacity-50 active:scale-[0.98]"
            style={{ background: transmission ? 'rgba(99,102,241,0.3)' : 'rgba(99,102,241,0.7)', border: '1px solid rgba(99,102,241,0.5)' }}
            onMouseEnter={e => { if (!transmission) e.currentTarget.style.boxShadow = '0 0 20px rgba(99,102,241,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
          >
            {transmission
              ? <><span className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Transmission...</>
              : <><Send size={12} /> Transmettre</>
            }
          </button>
        )}

        {/* Retour au choix de mode */}
        {mode && !enregistrement && (
          <button
            onClick={() => { setMode(null); recommencer(); }}
            className="w-full text-center text-[8px] font-mono uppercase tracking-widest text-white/20 hover:text-white/50 transition-colors py-1"
          >
            ← Changer de format
          </button>
        )}
      </div>
    </div>
  );
}